import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { farmvestService } from '../../../services/farmvest_api';
import { LeaveRequest } from '../../../types/farmvest';

export const fetchLeaveRequests = createAsyncThunk(
    'farmvestLeaveRequests/fetchLeaveRequests',
    async (params: { status?: string; page?: number; size?: number; search?: string; date?: string } | undefined, { rejectWithValue, signal }) => {
        try {
            const response = await farmvestService.getLeaveRequests(params, signal);
            const rawData = response.data || [];

            // Normalize ID field - searching for any possible ID field the backend might use
            const leaveRequests = rawData.map((item: any, index: number) => ({
                ...item,
                // Fallback to index if no ID is found to prevent duplicate "undefined" IDs
                id: item.id || item.leave_id || item.leave_request_id || item.ticket_id || (index + 1)
            }));

            return {
                leaveRequests,
                count: response.count || 0,
                pagination: response.pagination || null,
                statusCounts: response.status_counts || null
            };
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch leave requests');
        }
    }
);

export const createLeaveRequest = createAsyncThunk(
    'farmvestLeaveRequests/createLeaveRequest',
    async (payload: any, { rejectWithValue, dispatch }) => {
        try {
            const response = await farmvestService.createLeaveRequest(payload);
            dispatch(fetchLeaveRequests(undefined));
            return response;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to create leave request');
        }
    }
);

export const updateLeaveStatus = createAsyncThunk(
    'farmvestLeaveRequests/updateLeaveStatus',
    async ({ leaveId, status, rejection_reason }: { leaveId: number; status: string; rejection_reason?: string }, { rejectWithValue, dispatch }) => {
        try {
            const response = await farmvestService.updateLeaveStatus(leaveId, {
                status,
                rejection_reason,
                leaves_id: leaveId
            });
            dispatch(fetchLeaveRequests(undefined));
            return response;
        } catch (error: any) {
            const detail = error.response?.data?.detail;
            const msg = typeof detail === 'string' ? detail :
                (Array.isArray(detail) && detail[0]?.msg) ? detail[0].msg :
                    detail?.msg || error.message || 'Failed to update leave status';
            return rejectWithValue(msg);
        }
    }
);

interface LeaveRequestsState {
    leaveRequests: LeaveRequest[];
    count: number;
    loading: boolean;
    createLoading: boolean;
    actionLoading: number | null;
    error: string | null;
    successMessage: string | null;
    pagination: { current_page: number; total_pages: number; total_items: number } | null;
    statusCounts: {
        PENDING: number;
        APPROVED: number;
        REJECTED: number;
        CANCELLED: number;
        all: number;
    } | null;
}

const initialState: LeaveRequestsState = {
    leaveRequests: [],
    count: 0,
    loading: false,
    createLoading: false,
    actionLoading: null,
    error: null,
    successMessage: null,
    pagination: null,
    statusCounts: null,
};

const leaveRequestsSlice = createSlice({
    name: 'farmvestLeaveRequests',
    initialState,
    reducers: {
        clearLeaveMessages: (state) => {
            state.error = null;
            state.successMessage = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchLeaveRequests.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchLeaveRequests.fulfilled, (state, action) => {
                state.loading = false;
                state.leaveRequests = action.payload.leaveRequests;
                state.count = action.payload.count;
                state.pagination = action.payload.pagination;
                state.statusCounts = action.payload.statusCounts;
            })
            .addCase(fetchLeaveRequests.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(createLeaveRequest.pending, (state) => {
                state.createLoading = true;
                state.error = null;
                state.successMessage = null;
            })
            .addCase(createLeaveRequest.fulfilled, (state, action) => {
                state.createLoading = false;
                state.successMessage = action.payload?.message || 'Leave request created successfully';
            })
            .addCase(createLeaveRequest.rejected, (state, action) => {
                state.createLoading = false;
                state.error = typeof action.payload === 'string' ? action.payload : JSON.stringify(action.payload);
            })
            .addCase(updateLeaveStatus.pending, (state, action) => {
                state.actionLoading = action.meta.arg.leaveId;
                state.error = null;
            })
            .addCase(updateLeaveStatus.fulfilled, (state, action) => {
                state.actionLoading = null;
                state.successMessage = action.payload?.message || 'Leave status updated';
            })
            .addCase(updateLeaveStatus.rejected, (state, action) => {
                state.actionLoading = null;
                state.error = typeof action.payload === 'string' ? action.payload : JSON.stringify(action.payload);
            });
    },
});

export const { clearLeaveMessages } = leaveRequestsSlice.actions;
export const leaveRequestsReducer = leaveRequestsSlice.reducer;
export default leaveRequestsSlice.reducer;
